/* eslint-disable @typescript-eslint/no-unused-vars */
import { ELSLoggingService } from '@els/els-ui-common-react';
import {
  buildUrlFromBase,
  eolsBaseApi,
  withApiErrorLoggingOnly,
  withApiErrorLoggingOnlyRaw
} from '../../utilities/api.utilities';
import {
  addSearchParams,
} from '../../utilities/app.utilities';
import {
  AdminChatIsbnBookDto,
  ChatDto,
  ChatEntitlementDto,
  ChatEntitlementTypeDto,
  ChatEntryDto,
  ChatEntryEvaluationDto,
  ChatPaginationResponseDto,
  ChatPerformanceDto,
  ChatReferenceDto,
  ChatUserRoleDto,
  ContextRequestDto,
  CreatePracticeQuizRequestDto,
  CreatePracticeQuizResponseDto,
  EbookCfiSsoUrlRequestDto,
  EbookSsoUrlRequestDto,
  EbookSsoUrlResponseDto,
  FlashcardResponseDto,
  FollowUpQuestionDto,
  GeneratedQuizQuestionDto,
  LlmPromptDto,
  LlmResponseDto,
  PostProcessJobDto,
  PostProcessJobTypeDto,
  QuizQuestionListResponseDto,
  QuizQuestionQueryParamsDto,
  RelatedContentDto,
  ShellsDto,
  StudentThemesStatusDto,
  VectorSearchDto,
  VectorSearchResponseDto,
} from './nhe-ai-chat-service.dtos';
import { ServerConstants } from '../../components/app/server.constants';
import { AppState } from '../../components/app/app-state.types';
import { isFeatureFlagEnabled } from '../../utilities/featureFlag.utilities';
import { FEATURE_FLAG } from '../eols-features-api/eols-features-api.constants';
import {
  getContextForEntry,
  getVstLaunchIsbn
} from '../../pages/ai-chat/ai-chat.utilities';

const fileName = 'nhe-ai-chat-service.utilities';

export const fetchChats = (props: {
  context: ContextRequestDto[];
  contextId: string;
  contextType: string;
  page: number;
  pageSize: number;
  userRole: ChatUserRoleDto;
  appState: AppState;
}): Promise<ChatPaginationResponseDto & {
  content: ChatDto[];
}> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(buildUrlFromBase(addSearchParams(`/nacs/chats`, {
      contextId: props.contextId,
      contextType: props.contextType,
      page: props.page,
      pageSize: props.pageSize,
      userRole: props.userRole,
      context: props.context,
    })));
  });
};

export const fetchChat = (id: number): Promise<ChatDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(buildUrlFromBase(`/nacs/chat/${id}`));
  });
};

export const deleteChat = (id: number): Promise<void> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.delete(buildUrlFromBase(`/nacs/chat/${id}`));
  });
};

export const postChat = (chat: Omit<ChatDto, 'id'>): Promise<ChatDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.post(buildUrlFromBase(`/nacs/chat`), { data: chat });
  });
};

export const putChat = (chat: ChatDto): Promise<ChatDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.put(buildUrlFromBase(`/nacs/chat/${chat.id}`), { data: chat });
  });
};

export const fetchChatEntries = (chatId: number, page: number, pageSize: number): Promise<ChatPaginationResponseDto & {
  content: ChatEntryDto[];
}> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(buildUrlFromBase(addSearchParams(`/nacs/chat/${chatId}/entries`, {
      page,
      pageSize
    })));
  });
};

export const postChatEntry = (entry: ShellsDto, chatId: number): Promise<ChatEntryDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.post(buildUrlFromBase(`/nacs/chat/${chatId}/entry`), { data: entry });
  });
};

export const postChatPrompt = (entry: ShellsDto, chatId: number): Promise<ChatEntryDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.post(buildUrlFromBase(`/nacs/chat/${chatId}/prompt`), { data: entry });
  });
};

export const postChatEntryShells = (entry: ShellsDto, chatId: number): Promise<ChatEntryDto[]> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.post(buildUrlFromBase(`/nacs/chat/${chatId}/entry/shells`), { data: entry });
  });
};

export const putChatEntry = (entry: ChatEntryDto): Promise<ChatEntryDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.put(buildUrlFromBase(`/nacs/chat/${entry.chatId}/entry/${entry.id}`), { data: entry });
  });
};

export const fetchChatEntry = (chatId: number, entryId: number): Promise<ChatEntryDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(buildUrlFromBase(`/nacs/chat/${chatId}/entry/${entryId}`));
  });
};


export const fetchFollowUpQuestions = (botEntryId: number): Promise<FollowUpQuestionDto[]> => {
  const followUpQueriesFullPath = getFollowUpQuestionsPath();

  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(buildUrlFromBase(addSearchParams(followUpQueriesFullPath, { botEntryId })));
  });
};

export const putFollowUpQuestion = (followUpQuestion: FollowUpQuestionDto): Promise<string[]> => {
  const followUpQueriesFullPath = getFollowUpQuestionsPath();

  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.put(buildUrlFromBase(followUpQueriesFullPath), { data: followUpQuestion });
  });
};

export const deleteChatEntry = (chatId: string, entryId: string): Promise<void> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.delete(buildUrlFromBase(`/nacs/chat/${chatId}/entry/${entryId}`));
  });
};

export const postChatEvaluation = (chatId: number, evaluation: Partial<ChatEntryEvaluationDto>): Promise<ChatEntryEvaluationDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.post(buildUrlFromBase(`/nacs/chat/${chatId}/entry/${evaluation.entryId}/evaluation`), { data: evaluation });
  });
};

export const putReferenceVideoData = (referenceId: number): Promise<ChatReferenceDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.put(buildUrlFromBase(addSearchParams(`/nacs/entry/reference/${referenceId}`, { updateVideoData: true })));
  });
};

export const putChatEvaluation = (chatId: number, evaluation: ChatEntryEvaluationDto): Promise<ChatEntryEvaluationDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.put(buildUrlFromBase(`/nacs/chat/${chatId}/entry/${evaluation.entryId}/evaluation/${evaluation.id}`), { data: evaluation });
  });
};

export const fetchChatEvaluations = (chatId: number): Promise<ChatEntryEvaluationDto[]> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(buildUrlFromBase(`/nacs/chat/${chatId}/entry/evaluations`));
  });
};

export const fetchChatPerformance = (
  startDate: string,
  numOfDays: number,
  courseSectionIds: number[]
): Promise<ChatPerformanceDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.post(buildUrlFromBase(`/nacs/student-engagement`), {
      data: {
        startDate,
        numOfDays,
        courseSectionIds
      }
    });
  });
};

export const postStudentThemes = (startDate: string, numOfDays: number, courseSectionIds: number[]): Promise<void> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.post(buildUrlFromBase(`/nacs/student-themes-cache`), {
      data: {
        startDate,
        numOfDays,
        courseSectionIds,
      }
    });
  });
};

export const fetchStudentThemesStatus = (startDate: string, numOfDays: number, courseSectionIds: number[]): Promise<StudentThemesStatusDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(buildUrlFromBase(addSearchParams(`/nacs/student-themes/status`, {
      startDate,
      numOfDays,
      courseSectionIds,
    })));
  });
};

export const adminSearchVector = (body: VectorSearchDto): Promise<VectorSearchResponseDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.post(buildUrlFromBase(`/nacs/admin/hm-platform/search/vector`), { data: body });
  });
};

export const adminPrompt = (body: LlmPromptDto): Promise<LlmResponseDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.post(buildUrlFromBase('/nacs/admin/cerebus/prompt'), { data: body });
  });
};

export const fetchChatEeoIsbns = (): Promise<string[]> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(buildUrlFromBase(`/nacs/chat/eeoisbns`));
  });
};

export const fetchAdminChatEeoIsbns = (isbn: string[]): Promise<AdminChatIsbnBookDto[]> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(buildUrlFromBase(addSearchParams(`/nacs/admin/chat/isbn`, { isbn })));
  });
};

export const fetchChatEntitlements = (isOsmosis: boolean, appState: AppState): Promise<Partial<Record<ChatEntitlementTypeDto, ChatEntitlementDto[]>>> => {
  const params: { isOsmosis?: boolean; contextType?: string; isbn?: string } = { isOsmosis };

  if (isFeatureFlagEnabled(FEATURE_FLAG.DISABLE_IS_OSMOSIS_BOOLEAN, appState.featureFlagsGrouped, appState.courseSectionId)) {
    delete params.isOsmosis;
  }

  const context = getContextForEntry(appState, null);

  // Include contextType if available
  if (context) {
    params.contextType = context.contextType;
  }

  // Send the VST launch ISBN so the service resolves entitlements for the ISBN actually being
  // launched rather than the token's authorizedIsbns. This lets QE test with an admin token whose
  // authorizedIsbns do not match the ISBN under test; NACS rejects the param unless the caller has
  // admin privileges or the token already authorizes the ISBN, so real users are unaffected.
  const vstLaunchIsbn = getVstLaunchIsbn(appState);
  if (vstLaunchIsbn) {
    params.isbn = vstLaunchIsbn;
  }

  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(buildUrlFromBase(addSearchParams('/nacs/chat/isbn/entitled',  params)));
  });
};

export const adminFetchChatEntry = (entryId: string): Promise<ChatEntryDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(buildUrlFromBase(`/nacs/admin/entry/${entryId}`));
  });
};

export const adminFetchChatEntries = (chatId: string, page: number, pageSize: number): Promise<ChatPaginationResponseDto & {
  content: ChatEntryDto[];
}> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(buildUrlFromBase(addSearchParams(`/nacs/admin/chat/${chatId}/entries`, { page, pageSize })));
  });
};

export const adminFetchChat = (chatId: string): Promise<ChatDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(buildUrlFromBase(`/nacs/admin/chat/${chatId}`));
  });
};

export const fetchRelatedContent = (botEntryId: number): Promise<RelatedContentDto[]> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(buildUrlFromBase(addSearchParams('/nacs/entry/related-content', { botEntryId })));
  });
};

export const getRelatedContentImageUrl = (id: number, token: string): string => {
  const baseUrl = ServerConstants[ServerConstants.DataSource].nacsBaseUrl;

  return addSearchParams(`${baseUrl}/nacs/entry/related-content/${id}/image`, { token });
};

export const fetchFlashcards = (botEntryId: number): Promise<FlashcardResponseDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(buildUrlFromBase(addSearchParams(`/nacs/flashcards`, { botEntryId })));
  });
};

export const getFollowUpQuestionsPath = (): string => {
  return '/nacs/entry/follow-up-question';
};

export const fetchEbookSsoUrl = (
  request: EbookSsoUrlRequestDto
): Promise<EbookSsoUrlResponseDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.post(
      buildUrlFromBase('/nacs/ebook/sso-url'),
      { data: request }
    );
  });
};

/**
 * Resolves to null when NACS reports no content for the CFI (a 204 with an empty
 * body, which axios resolves as a success rather than rejecting) so callers can
 * branch on "no content" without inspecting the response status.
 *
 * This keys off the HTTP status rather than signInUrl truthiness so that a
 * malformed 2xx (a server defect) is not silently reported as the sanctioned
 * "no content for this CFI" outcome. The shared withApiErrorLoggingOnly helper
 * unwraps to response.data and discards the status, so this uses the status-
 * preserving withApiErrorLoggingOnlyRaw variant, which logs failures under the same
 * source as every other call in this module.
 */
export const fetchEbookCfiSsoUrl = (
  request: EbookCfiSsoUrlRequestDto
): Promise<EbookSsoUrlResponseDto | null> => {
  return withApiErrorLoggingOnlyRaw<EbookSsoUrlResponseDto>(() => {
    return eolsBaseApi.post<EbookSsoUrlResponseDto>(
      buildUrlFromBase('/nacs/ebook/sso-url/cfi'),
      { data: request }
    )
      .then((response) => {
        if (response?.status !== 204 && !response?.data?.signInUrl) {
          // Unexpected shape: a 2xx that is not the documented 204 but carries no
          // signInUrl. Reject rather than mislabelling it as the sanctioned "no
          // content" outcome, so it is reported as an API_ERROR (and logged by the
          // wrapper); the caller's page-range fallback still runs off the rejection.
          throw new Error(
            `fetchEbookCfiSsoUrl: ${response?.status} response missing signInUrl`
          );
        }
        return response;
      });
  })
    .then((response) => {
      return response.status === 204 ? null : response.data;
    });
};

export const patchFollowUpQuestion = (followUpQuestionId: number): Promise<void> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.patch(buildUrlFromBase(addSearchParams('/nacs/entry/follow-up-question/select', { id: followUpQuestionId })));
  });
};

export const fetchPostProcessJobs = (params: {
  chatId?: number;
  entryId?: number;
  jobType?: PostProcessJobTypeDto;
}): Promise<PostProcessJobDto[]> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(buildUrlFromBase(addSearchParams(`/nacs/post-process-jobs`, {
      chatId: params.chatId,
      entryId: params.entryId,
      jobType: params.jobType,
    })));
  });
};

export const fetchGeneratedQuizQuestions = (botEntryId: number): Promise<GeneratedQuizQuestionDto[]> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(buildUrlFromBase(addSearchParams(`/nacs/assessment/generated-questions`, { botEntryId })));
  });
};

// userRole and context scope the questions to the chats "Your chats" shows, as they do for fetchChats.
// The filters go in the body, not the query string: the gateway turns an encoded "+" in a query string into a
// space, so searching for "+" would match every question. The POST only reads.
export const fetchQuizQuestions = (props: {
  params: QuizQuestionQueryParamsDto;
  userRole: ChatUserRoleDto;
  context: ContextRequestDto[];
}): Promise<QuizQuestionListResponseDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.post(
      buildUrlFromBase(addSearchParams(`/nacs/assessment/quiz-questions/search`, {
        userRole: props.userRole,
        context: props.context,
      })),
      { data: props.params }
    );
  });
};

export const createPracticeQuiz = (props: {
  request: CreatePracticeQuizRequestDto;
  userRole: ChatUserRoleDto;
  context: ContextRequestDto[];
}): Promise<CreatePracticeQuizResponseDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.post(
      buildUrlFromBase(addSearchParams(`/nacs/assessment/practice-quiz`, {
        userRole: props.userRole,
        context: props.context,
      })),
      { data: props.request }
    );
  });
};
